import { useState, useCallback, useEffect } from "react";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8787";
const TOKEN_KEY = "starbridge-auth-token";
const USER_KEY = "starbridge-auth-user";

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [token, setToken] = useState<string | null>(() => {
    try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
  });
  const [isValidating, setIsValidating] = useState(!!token); // initial mount token check
  const [isBusy, setIsBusy] = useState(false); // signup/login in progress
  const [error, setError] = useState<string | null>(null);

  const persist = useCallback((t: string, u: AuthUser) => {
    setToken(t);
    setUser(u);
    try {
      localStorage.setItem(TOKEN_KEY, t);
      localStorage.setItem(USER_KEY, JSON.stringify(u));
    } catch {}
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setError(null);
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    } catch {}
  }, []);

  // Validate existing token on mount
  useEffect(() => {
    if (!token) { setIsValidating(false); return; }

    fetch(`${API_BASE}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          setUser(data.user);
          try { localStorage.setItem(USER_KEY, JSON.stringify(data.user)); } catch {}
        } else {
          logout();
        }
      })
      .catch(() => logout())
      .finally(() => setIsValidating(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const signup = useCallback(async (email: string, password: string, displayName: string) => {
    setError(null);
    setIsBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, displayName }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Signup failed");
        return false;
      }
      persist(data.token, data.user);
      return true;
    } catch (err) {
      setError("Connection failed");
      return false;
    } finally {
      setIsBusy(false);
    }
  }, [persist]);

  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    setIsBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Login failed");
        return false;
      }
      persist(data.token, data.user);
      return true;
    } catch (err) {
      setError("Connection failed");
      return false;
    } finally {
      setIsBusy(false);
    }
  }, [persist]);

  return {
    user,
    token,
    isLoading: isValidating, // only true during initial mount validation
    isBusy, // true during signup/login API calls
    error,
    isAuthenticated: !!user && !!token,
    signup,
    login,
    logout,
    clearError: useCallback(() => setError(null), []),
  };
}
